import { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { alpha } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { currentEnv, Env } from 'modules/common';
import { dashboardRoutesConfig } from 'modules/dashboard';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { useTranslation } from 'modules/i18n';
import { LogoIcon } from 'modules/icons';

import { Account } from '../Account';
import { Navigation } from '../Navigation';
import MenuIcon from './assets/menu.svg?react';
import { translation } from './translation';

interface IHeaderProps {
  className?: string;
}

export function Header({ className }: IHeaderProps): ReactElement {
  const { classes, cx } = useStyles();
  const { t, keys } = useTranslation(translation);
  const { onOpen } = useDialog(KnownDialogs.navigation);

  return (
    <header className={cx(classes.root, className)}>
      <div className={classes.navigation}>
        <Navigation />
      </div>

      <Link
        className={classes.logoLink}
        to={dashboardRoutesConfig.dashboard.generatePath()}
      >
        <LogoIcon className={classes.logo} />

        {currentEnv !== Env.prod && (
          <div className={classes.label}>{t(keys.testnet)}</div>
        )}
      </Link>

      <div className={classes.right}>
        <Account />

        <MenuIcon className={classes.mobileMenu} onClick={onOpen} />
      </div>
    </header>
  );
}

export const useStyles = makeStyles()(theme => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(5, 10),
    margin: '0 auto',
    maxWidth: 1728,

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(5, 4),
    },
  },

  logoLink: {
    display: 'flex',
    position: 'relative',
  },

  label: {
    position: 'absolute',
    top: 0,
    right: -70,
    background: alpha(theme.palette.secondary.contrastText, 0.1),
    color: theme.palette.secondary.contrastText,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    fontSize: 12,

    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
      padding: theme.spacing(0.5, 1),
      top: 31,
      right: -2,
    },
  },

  logo: {
    width: 111,
  },

  right: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
  },

  navigation: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    left: 0,
    paddingRight: theme.spacing(8),
    top: 0,

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },

  mobileMenu: {
    cursor: 'pointer',

    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
}));
